import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

import { useLegalDocument } from '../legal/useLegalDocument';
import { LegalNotice } from '../legal/LegalNotice';
import { beginLegalAttempt, completeLegalAttempt, cancelLegalAttempt } from '../legal/legalOutbox';
import type { NoticeVariant } from '../legal/legalTypes';

import type { BrowserKind } from './accountState';
import type { BoardSummary } from './accountState';
import { captureHeadline, tapCaptureHeadline, tapCaptureLine } from './boardNaming';
import { BoardPeopleRow } from './BoardPeopleRow';
import type { DismissReason } from './captureTiming';
import { suggestEmail } from './emailSuggest';
import { useDialogA11y } from '../hooks/useDialogA11y';
import { reportAccountCreated } from '../hooks/useGaUserIdentity';
import { prepareAppleSignIn, type AppleSignInRequest } from '../auth/appleWebSignIn';
import {
  v4SignInWithGoogle,
  v4SignInWithApple,
  v4SignInWithEmailPassword,
  v4CreateAccountWithEmailPassword,
  v4SendPasswordReset,
  type V4AuthResult,
  type V4AuthAccount,
} from '../auth/v4Auth';
import {
  useAuthConfirmation,
  stashRedirectConfirmation,
  clearRedirectConfirmation,
} from '../auth/authConfirmation';
import type { RedirectOutcome } from '../auth/redirectOutcome';

/** Shared Firebase login/signup form. Meta webviews use email/password only. */
export type AccountMode = 'login' | 'create';

const Scrim = styled.div`
  position: fixed;
  inset: 0;
  z-index: 80;
  background: rgba(28, 22, 17, 0.44);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  animation: v4SignInFade 160ms ease both;
  @keyframes v4SignInFade { from { opacity: 0; } to { opacity: 1; } }
  @media (min-width: 640px) { align-items: center; }
  @media (prefers-reduced-motion: reduce) { animation: none; }
`;

const Card = styled.div`
  box-sizing: border-box;
  width: 100%;
  max-width: 420px;
  max-height: 90dvh;
  overflow-y: auto;
  overscroll-behavior: contain;
  background: ${({ theme }) => theme.color.creamLight};
  border-top-left-radius: 26px;
  border-top-right-radius: 26px;
  box-shadow: 0 -8px 32px rgba(80, 40, 20, 0.18);
  padding: 22px 26px calc(30px + env(safe-area-inset-bottom));
  @media (min-width: 640px) {
    border-radius: 26px;
    box-shadow: 0 24px 48px rgba(80, 40, 20, 0.22);
    padding-bottom: 30px;
  }
`;

const Grabber = styled.div`
  width: 48px;
  height: 20px;
  margin: -12px auto 12px;
  touch-action: none;
  &::after { content: ''; display: block; height: 4px; border-radius: 3px; background: #cec5bc; }
  @media (min-width: 640px) { display: none; }
`;

const Heading = styled.h2`
  margin: 0 0 4px;
  font-family: ${({ theme }) => theme.font.serif};
  font-size: 24px;
  color: hsl(var(--foreground));
`;
const Subhead = styled.p`
  margin: 0 0 6px;
  font-family: ${({ theme }) => theme.font.sans};
  font-size: 14px;
  line-height: 1.45;
  color: hsl(var(--muted-foreground));
`;
const Btn = styled.button`
  width: 100%;
  border-radius: 28px;
  padding: 14px 16px;
  margin-top: 10px;
  font: inherit;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid ${({ theme }) => theme.color.warmBorder};
  background: #fff;
  color: hsl(var(--foreground));
  &:hover { background: ${({ theme }) => theme.color.cream}; }
  &:disabled { opacity: 0.55; cursor: default; }
`;
const PrimaryBtn = styled(Btn)`
  border: none;
  color: #fff;
  background: hsl(var(--primary));
  &:hover { background: ${({ theme }) => theme.color.clayPressed}; }
`;
// Google and Apple carry EQUAL weight — both bordered/white with the provider's
// brand mark, neither pre-selected. (Before Kate's 2026-08-20 QA, Google was the
// filled PrimaryBtn and Apple a plain Btn, which read as "Google is the answer".)
const ProviderBtn = styled(Btn)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
`;
const ProviderMark = styled.span`
  display: inline-flex;
  flex: 0 0 auto;
  line-height: 0;
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  margin: 20px 0 6px;
  font-size: 13px;
  color: ${({ theme }) => theme.color.mutedText};
  &::before, &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: ${({ theme }) => theme.color.warmBorder};
  }
`;

const Field = styled.input`
  box-sizing: border-box;
  width: 100%;
  height: 50px;
  margin-top: 10px;
  padding: 0 14px;
  border: 1.5px solid ${({ theme }) => theme.color.warmBorder};
  border-radius: 12px;
  background: ${({ theme }) => theme.color.cream};
  font: inherit;
  font-size: 16px;
  color: hsl(var(--foreground));
  &:focus { outline: none; border-color: hsl(var(--primary)); }
`;
// The did-you-mean row, ported from V4WebviewSignIn (THE-485). Same three
// load-bearing behaviours as the reference: it SUGGESTS and never autocorrects
// (the address changes only when she taps the link), it never blocks submit
// (a hint, not a validation error), and it is rendered only when suggestEmail
// returns non-null so the row collapses to zero height instead of reserving a
// permanent layout hole under the field.
const SuggestRow = styled.p`
  margin: 8px 2px 0;
  font-family: ${({ theme }) => theme.font.sans};
  font-size: 13.5px;
  color: ${({ theme }) => theme.color.mutedText};
  button {
    background: none;
    border: 0;
    padding: 0;
    font: inherit;
    color: hsl(var(--primary));
    text-decoration: underline;
    text-underline-offset: 2px;
    cursor: pointer;
  }
`;
// Password field with an inline show/hide toggle.
const PasswordRow = styled.div`
  position: relative;
  margin-top: 10px;
`;
const PasswordInput = styled(Field)`
  margin-top: 0;
  padding-right: 68px;
`;
const ShowHideBtn = styled.button`
  position: absolute;
  top: 50%;
  right: 8px;
  transform: translateY(-50%);
  background: none;
  border: 0;
  padding: 6px 8px;
  font: inherit;
  font-size: 13px;
  font-weight: 500;
  color: hsl(var(--primary));
  cursor: pointer;
`;
const ErrorText = styled.p`
  margin: 12px 0 0;
  font-size: 13.5px;
  color: ${({ theme }) => theme.color.errorRed};
`;
// The lost-redirect terminal state (THE-580). Sits ABOVE the provider buttons,
// not below the form, because it is an explanation OF those buttons: the user
// just tapped one and nothing happened, and the whole failure mode is that the
// screen looks identical to never having tapped at all. Styled as a notice
// rather than ErrorText — nothing the user did was wrong, and the message's job
// is to hand them the working door, not to scold.
const RedirectLostNotice = styled.p`
  margin: 12px 0 0;
  padding: 10px 12px;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.color.warmBorder};
  background: ${({ theme }) => theme.color.cream};
  font-family: ${({ theme }) => theme.font.sans};
  font-size: 13.5px;
  line-height: 1.45;
  color: hsl(var(--foreground));
`;
const TextLink = styled.button`
  display: block;
  margin: 14px auto 0;
  background: none;
  border: 0;
  font: inherit;
  font-size: 13px;
  color: ${({ theme }) => theme.color.mutedText};
  text-decoration: underline;
  text-underline-offset: 2px;
  cursor: pointer;
`;
/* Inline brand marks — self-contained so the modal makes no external request,
   and offline-deterministic for Happo. aria-hidden: the button's own text is the
   accessible name. */
const GoogleMark: React.FC = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden focusable="false">
    <path
      fill="#4285F4"
      d="M17.64 9.2045c0-.6381-.0573-1.2518-.1636-1.8409H9v3.4814h4.8436c-.2086 1.125-.8427 2.0782-1.7959 2.7164v2.2581h2.9082c1.7018-1.5668 2.6841-3.874 2.6841-6.615z"
    />
    <path
      fill="#34A853"
      d="M9 18c2.43 0 4.4673-.806 5.9564-2.1805l-2.9082-2.2581c-.8059.54-1.8368.859-3.0482.859-2.344 0-4.3282-1.5827-5.0359-3.7104H.9573v2.3318C2.4382 15.9832 5.4818 18 9 18z"
    />
    <path
      fill="#FBBC05"
      d="M3.9641 10.71c-.18-.54-.2823-1.1168-.2823-1.71s.1023-1.17.2823-1.71V4.9582H.9573C.3477 6.1732 0 7.5477 0 9s.3477 2.8268.9573 4.0418L3.9641 10.71z"
    />
    <path
      fill="#EA4335"
      d="M9 3.5795c1.3214 0 2.5077.4541 3.4405 1.346l2.5813-2.5814C15.4632.8918 13.4959 0 9 0 5.4818 0 2.4382 2.0168.9573 4.9582L3.9641 7.29C4.6718 5.1623 6.656 3.5795 9 3.5795z"
    />
  </svg>
);
const AppleMark: React.FC = () => (
  <svg width="16" height="18" viewBox="0 0 17 20" aria-hidden focusable="false">
    <path
      fill="currentColor"
      d="M14.06 10.64c-.02-2.2 1.8-3.26 1.88-3.31-1.03-1.5-2.62-1.71-3.55-1.73-1.51-.15-2.95.89-3.72.89-.77 0-1.95-.87-3.2-.85-1.65.02-3.17.96-4.02 2.44-1.71 2.97-.44 7.37 1.23 9.78.81 1.18 1.78 2.5 3.05 2.45 1.22-.05 1.68-.79 3.16-.79 1.48 0 1.89.79 3.19.76 1.32-.02 2.15-1.2 2.96-2.39.93-1.37 1.31-2.69 1.33-2.76-.03-.01-2.56-.98-2.58-3.9zM11.6 4.09c.68-.82 1.14-1.96 1.01-3.09-.98.04-2.16.65-2.86 1.47-.63.73-1.18 1.89-1.03 3 .1.09 1.6.06 2.88-1.38z"
    />
  </svg>
);

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Grace period between "we handed the browser a redirect" and "that redirect
 * did not happen" (THE-580). On the happy path the document is destroyed long
 * before this fires. It is the in-session half of lost-redirect detection: it
 * needs no storage and no cross-load signal at all, so it is the layer that
 * still works when the return lands in a fresh tab and every other trace of the
 * attempt is gone. 1.5s is comfortably longer than the gap between
 * `signInWithRedirect` resolving and the navigation committing, and short
 * enough that the user has not yet decided the button is broken.
 */
const OAUTH_STALL_GRACE_MS = 1_500;

type OAuthProviderKey = 'google' | 'apple';

export interface V4SignInModalProps {
  open: boolean;
  onClose: () => void;
  browser: BrowserKind;
  intent?: 'signin' | 'keep';
  boardCount?: number;
  boards?: BoardSummary[];
  /**
   * Which headline/pills wording `boards` feeds (THE-975). `'timedPrompt'`
   * (the default) is today's `captureHeadline` — THE-962's board reminder,
   * the one existing caller of `boards` before this prop existed, and it
   * must keep seeing exactly that regardless of what else lands here.
   * `'tap'` is the new copy for every entry that opens this sheet with a tap
   * (header/You-tab/account-menu "Log in"/"Sign in"): a different headline
   * function, a lower pill threshold, and a headline/pills that hide behind
   * the "Already have an account? Log in" toggle. Call sites decide which —
   * see the PR description for why this isn't a branch inside the shared
   * `boards`-having behavior.
   */
  variant?: 'tap' | 'timedPrompt';
  initialMode?: AccountMode;
  /**
   * `account` is Firebase's OWN verdict (`getAdditionalUserInfo().isNewUser`,
   * threaded through `V4AuthResult.outcome.account`) — not the in-sheet mode
   * toggle. The two can disagree: she can switch to "Log in" from a sheet
   * whose heading still says "Create account", or tap a provider button that
   * resolves to an existing account either way. A caller that cares whether
   * this was a NEW signup (the capture sheet does — see
   * `V4BoardCaptureHost.handleSignedIn`) must branch on this, not on
   * `initialMode`/`mode`.
   */
  onSignedIn?: (account: V4AuthAccount) => void;
  onDismiss?: (reason: DismissReason) => void;
  testId?: string;
  /**
   * One line of context under the heading, supplied by the ENTRY that opened
   * this sheet. Absent at every other door, which is the point: `/board/:id`
   * presents this modal as a LANDING for a logged-out visitor (THE-797) and
   * needs to say why, without that sentence appearing on the header pill or the
   * capture sheet. No entry switch lives in here.
   */
  contextLine?: string;
  /**
   * The redirect outcome published by the SINGLE owner of `getRedirectResult`
   * (`AuthGateProvider`), injected as a PROP so `v4/` stays free of the
   * sign-in-door context — same arrangement as `V4ClaimRoute`. Defaults to
   * 'pending' so every other mount site (capture sheet, home door, public door)
   * is unaffected and simply never shows the terminal state.
   */
  redirectOutcome?: RedirectOutcome;
  /**
   * True when the host determined THIS page load is the return leg of an OAuth
   * redirect (see `auth/redirectReturn.ts`). Required alongside a terminal
   * `redirectOutcome`, because `no-result` is also what an ordinary cold load
   * looks like — without this the modal would tell a first-time visitor their
   * sign-in failed.
   */
  oauthReturn?: boolean;
  /**
   * How long to wait after a mobile redirect is handed to the browser before
   * calling it stalled. `signInWithRedirect` resolves its promise a beat BEFORE
   * the navigation commits, so reacting immediately would flash "didn't finish"
   * across a redirect that is working. Overridable for tests.
   */
  oauthStallGraceMs?: number;
  /**
   * True only for the mount inside the "Don't lose {name}'s board" capture
   * sheet (`V4CaptureSheet`). Other doors also pass `boards` (e.g. the
   * `/board/:id` landing backdrop), so `boardCount > 0` alone can't tell the
   * capture sheet apart from them — this flag rides the redirect-confirmation
   * stash so AuthGate's return-leg consumer can fire `capture_sheet_signed_up`
   * only for sheet-driven auth, same as the synchronous path's own gating
   * (THE-966).
   */
  fromCaptureSheet?: boolean;
}

export const V4SignInModal: React.FC<V4SignInModalProps> = ({
  open, onClose, browser, boardCount = 0, boards, variant = 'timedPrompt', initialMode = 'login',
  onSignedIn, onDismiss, testId = 'v4-signin-modal', contextLine,
  redirectOutcome = { status: 'pending' }, oauthReturn = false,
  oauthStallGraceMs = OAUTH_STALL_GRACE_MS, fromCaptureSheet = false,
}) => {
  const legal = useLegalDocument(open);
  const [mode, setMode] = useState<AccountMode>(initialMode);
  const noticeVariant: NoticeVariant = browser === 'webview' ? `${mode}_email` : mode;
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resetSent, setResetSent] = useState(false);
  const [appleRequest, setAppleRequest] = useState<AppleSignInRequest | null>(null);
  const [appleLoadFailed, setAppleLoadFailed] = useState(false);
  const [appleAttempt, setAppleAttempt] = useState(0);
  const [appleExistingAccount, setAppleExistingAccount] = useState(false);
  // THE-579. `appleFailed`: an Apple attempt in THIS modal session ended
  // without signing anyone in — Apple's cancel code is not trustworthy enough
  // to stay silent on, so every Apple failure gets a terminal state.
  const [appleFailed, setAppleFailed] = useState(false);
  // THE-580. `stalledProvider`: we handed the browser a redirect for this
  // provider and the grace period elapsed with the document still alive.
  // `attemptedSinceOpen`: the user has started a fresh attempt in this modal
  // session, which retires the PREVIOUS load's outcome — once they have tapped
  // again, "the last page load's redirect failed" is stale news and the only
  // thing worth reporting is what the new attempt does.
  const [stalledProvider, setStalledProvider] = useState<OAuthProviderKey | null>(null);
  const [attemptedSinceOpen, setAttemptedSinceOpen] = useState(false);
  const stallTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const { confirm } = useAuthConfirmation();
  const cardRef = useRef<HTMLDivElement>(null);
  const pending = useRef(false);
  const touchStartY = useRef<number | null>(null);
  const entryContext = boardCount > 0 ? 'guest_board' : 'generic';
  // Null whenever the module decides to stay quiet — the row below renders only on a hit.
  const emailSuggestion = suggestEmail(email);
  // THE-975. The tap variant's headline/line/pills hide behind the existing
  // "Already have an account? Log in" toggle — logging into an existing
  // account leaves the guest boards behind (as today), so once she's on the
  // login side of `mode` there is nothing board-shaped left to say. Reusing
  // `mode` rather than new state: it already flips on that toggle AND on the
  // OAuth-collision recovery below, and both are the same "she's logging into
  // an existing account now" moment.
  const isTapWithBoards = variant === 'tap' && !!boards && boards.length > 0;
  const showBoardsCopy = isTapWithBoards ? mode !== 'login' : !!boards;
  // "From one board up" for the tap variant (PRD), vs today's "two up" for
  // the timed prompt — unchanged for every caller that doesn't pass `variant`.
  const pillsThreshold = isTapWithBoards ? 1 : 2;
  const title = variant === 'tap' && boards && boards.length > 0
    ? mode === 'login' ? 'Log in' : tapCaptureHeadline(boards)
    : boards ? captureHeadline(boards) : mode === 'login' ? 'Log in' : 'Create account';

  useEffect(() => {
    if (!open || browser === 'webview') return;
    let active = true;
    setAppleRequest(null);
    setAppleLoadFailed(false);
    prepareAppleSignIn().then(request => {
      if (active) setAppleRequest(request);
    }).catch(() => { if (active) setAppleLoadFailed(true); });
    return () => { active = false; };
  }, [open, browser, appleAttempt]);

  const dismiss = useCallback((reason: DismissReason) => {
    onDismiss?.(reason);
    onClose();
  }, [onClose, onDismiss]);
  useDialogA11y({ open, containerRef: cardRef,
    onEscape: () => dismiss('escape'), escapeCapture: true });

  useEffect(() => {
    if (open) {
      setMode(initialMode);
      setAppleExistingAccount(false);
      setAppleFailed(false);
      setEmail('');
      setPassword('');
      setConfirmPassword('');
      setShowPassword(false);
      setError(null);
      setResetSent(false);
      setStalledProvider(null);
      setAttemptedSinceOpen(false);
      clearTimeout(stallTimerRef.current);
    }
  }, [open, initialMode]);

  // Never fire the stall verdict into an unmounted tree.
  useEffect(() => () => clearTimeout(stallTimerRef.current), []);

  const authenticated = (res: V4AuthResult) => {
    // THE-1375: the door reports the verified outcome itself, at the moment
    // it's true, rather than a shared listener inferring it later from
    // auth-state timing. This is the ONE chokepoint every mount of this
    // modal runs through (homepage, board capture, public board, /you),
    // so it covers every popup/email entry point in one place.
    if (res.outcome.account === 'created') reportAccountCreated(res.credential.user);
    confirm({ ...res.outcome, entryContext, boardCount, verificationEmail: res.verificationEmail });
    onSignedIn?.(res.outcome.account);
    onClose();
  };

  const runOAuth = async (
    fn: () => Promise<V4AuthResult | null>,
    provider: OAuthProviderKey,
  ) => {
    if (pending.current || browser === 'webview' || !legal.document) return;
    pending.current = true;
    setBusy(true);
    setError(null);
    // A new attempt clears the previous verdict and retires the previous load's.
    clearTimeout(stallTimerRef.current);
    setStalledProvider(null);
    setAppleFailed(false);
    setAttemptedSinceOpen(true);
    stashRedirectConfirmation({ entryContext, boardCount, fromCaptureSheet });
    let agreementAttempt: string | undefined;
    try {
      agreementAttempt = beginLegalAttempt(legal.document, noticeVariant, provider);
      const res = await fn();
      if (res) {
        completeLegalAttempt(agreementAttempt, res.credential.user);
        clearRedirectConfirmation();
        authenticated(res);
      } else {
        // `null` means a MOBILE REDIRECT was initiated: v4Auth handed the
        // browser off to the provider and returned. If we are still running
        // after the grace period, that navigation never happened — the tap did
        // nothing, which is precisely the state that reads as a dead button and
        // produced the double-taps in the Identity Platform logs. On a redirect
        // that works, this document is gone before the timer fires.
        clearTimeout(stallTimerRef.current);
        stallTimerRef.current = setTimeout(() => {
          setStalledProvider(provider);
        }, oauthStallGraceMs);
      }
    } catch (err) {
      cancelLegalAttempt(agreementAttempt);
      clearRedirectConfirmation();
      const code = (err as { code?: string }).code;
      if (code === 'auth/apple-fresh-sign-in-required') {
        setAppleExistingAccount(true);
        setError('Your Apple account is already registered. Continue with Apple once more to log in.');
      } else if (provider === 'apple' && code !== 'auth/cancelled-popup-request') {
        setAppleFailed(true);
      } else if (code !== 'auth/popup-closed-by-user' && code !== 'auth/cancelled-popup-request') {
        // THE-576. A guest tapping Google/Apple takes the LINK path
        // (`linkWithPopup`/`linkWithCredential`), and a link whose provider
        // email already belongs to another Thea account fails
        // `auth/email-already-in-use` — NOT the sign-in path's
        // `auth/account-exists-with-different-credential`. Only the latter was
        // handled, so the former fell through to "That didn't go through. Try
        // again." — an invitation to repeat a failure that can never succeed.
        // Observed in production 2026-09-25 16:47-16:49Z: a feedback-session
        // user who had made a password account 25 minutes earlier retried
        // Google three times and gave up. Both codes mean the same thing to
        // the person on the other side, so they get the same recovery.
        const collision = code === 'auth/account-exists-with-different-credential'
          || code === 'auth/email-already-in-use';
        if (collision) {
          // Firebase attaches the provider's verified address to these two
          // errors. Prefilling it turns "use your existing login method" from
          // an instruction into a form that is already half-completed; the
          // password field below is the way out.
          const collidedEmail = (err as { customData?: { email?: unknown } })
            ?.customData?.email;
          if (typeof collidedEmail === 'string' && collidedEmail) {
            setEmail(collidedEmail);
            setResetSent(false);
          }
          setMode('login');
        }
        setError(collision
          ? 'You already have a Thea account with this email. Log in with your password below, or reset it.'
          : code === 'auth/popup-blocked'
            ? 'Allow pop-ups for Thea, then try again.'
            : 'That didn’t go through. Try again.');
      }
    } finally {
      pending.current = false;
      setBusy(false);
    }
  };

  const onEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (pending.current || !legal.document) return;
    const addr = email.trim();
    if (!EMAIL_RE.test(addr) || !password) {
      setError('Enter your email and password.');
      return;
    }
    if (mode === 'create' && password !== confirmPassword) {
      setError('Passwords don’t match. Please re-enter your password.');
      return;
    }
    pending.current = true;
    setBusy(true);
    setError(null);
    let agreementAttempt: string | undefined;
    try {
      agreementAttempt = beginLegalAttempt(legal.document, noticeVariant, mode);
      const action = mode === 'create' ? v4CreateAccountWithEmailPassword : v4SignInWithEmailPassword;
      const result = await action(addr, password);
      completeLegalAttempt(agreementAttempt, result.credential.user);
      authenticated(result);
    } catch (err) {
      cancelLegalAttempt(agreementAttempt);
      const code = (err as { code?: string }).code;
      const messages: Record<string, string> = {
        'auth/wrong-password': 'Email or password doesn’t match. Try again or reset your password.',
        'auth/invalid-credential': 'Email or password doesn’t match. Try again or reset your password.',
        'auth/user-not-found': 'Email or password doesn’t match. Try again or create an account.',
        'auth/email-already-in-use': 'Unable to create an account with this email. Try logging in or resetting your password.',
        'auth/credential-already-in-use': 'Unable to create an account with this email. Try logging in or resetting your password.',
        'auth/weak-password': 'Choose a stronger password with at least 6 characters.',
        'auth/password-does-not-meet-requirements': 'Choose a stronger password with uppercase and lowercase letters, a number, and a symbol.',
        'auth/too-many-requests': 'Too many attempts. Please try again later.',
        'auth/network-request-failed': 'Check your connection and try again.',
      };
      setError(messages[code ?? ''] ?? 'Something went wrong. Try again.');
    } finally {
      pending.current = false;
      setBusy(false);
    }
  };

  const resetPassword = async () => {
    if (pending.current) return;
    if (!EMAIL_RE.test(email.trim())) {
      setError('Enter your email above first.');
      return;
    }
    pending.current = true;
    setBusy(true);
    setError(null);
    setResetSent(false);
    try {
      await v4SendPasswordReset(email.trim());
      setResetSent(true);
    } catch (err) {
      // Keep account-existence responses neutral, but don't claim a network failure sent mail.
      if ((err as { code?: string }).code === 'auth/user-not-found') setResetSent(true);
      else setError('Couldn’t send the password email. Please try again.');
    } finally {
      pending.current = false;
      setBusy(false);
    }
  };

  // THE-580 — the terminal state for a lost OAuth redirect. Two independent
  // routes in, because they cover different failure shapes:
  //
  //   stalledProvider      — the redirect we started in THIS session never
  //                          navigated. Pure component state: no storage, no
  //                          cross-load signal, works in a brand-new tab.
  //   oauthReturn + lost   — the host saw this load come back from a provider
  //                          and `getRedirectResult` produced nothing. Covers
  //                          the user who comes back and re-opens the door.
  //
  // `attemptedSinceOpen` gates the second: after a fresh tap, the previous
  // load's outcome is stale and the in-session route owns the verdict.
  const redirectOutcomeIsLost =
    redirectOutcome.status === 'no-result' ||
    redirectOutcome.status === 'error' ||
    redirectOutcome.status === 'timeout';
  const showRedirectLost =
    !busy &&
    (stalledProvider !== null ||
      (!attemptedSinceOpen && oauthReturn && redirectOutcomeIsLost));
  const stalledProviderLabel =
    stalledProvider === 'google' ? 'Google' : stalledProvider === 'apple' ? 'Apple' : null;
  // THE-579 — Apple's terminal state. Apple is popup-only (its own SDK, no page
  // navigation), so none of the redirect machinery above ever sees it; without
  // this the drawer re-renders unchanged and the tap reads as a dead button.
  // Suppressed while `appleExistingAccount` is set, where the ErrorText is
  // already telling her to tap Apple once more and a "didn't finish" notice
  // beside it would contradict that instruction.
  const showAppleFailed = !busy && appleFailed && !appleExistingAccount;

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <Scrim data-testid={boards ? 'v4-capture-scrim' : 'v4-signin-scrim'} onClick={() => dismiss('scrim')}>
      <Card ref={cardRef} role="dialog" aria-modal="true" aria-label={title}
        data-testid={testId} onClick={(e) => e.stopPropagation()}>
        {boards ? <Grabber aria-hidden="true"
          onTouchStart={(e) => { touchStartY.current = e.touches[0]?.clientY ?? null; }}
          onTouchEnd={(e) => {
            if (touchStartY.current !== null && (e.changedTouches[0]?.clientY ?? 0) - touchStartY.current > 60) dismiss('swipe');
            touchStartY.current = null;
          }} /> : null}
        <Heading>{title}</Heading>
        {contextLine ? (
          <Subhead data-testid="v4-signin-context-line">{contextLine}</Subhead>
        ) : null}
        {boards && showBoardsCopy ? (
          <Subhead>
            {isTapWithBoards
              ? tapCaptureLine(boards.length)
              : 'Create an account to keep your boards, or log in to your account.'}
          </Subhead>
        ) : null}
        {boards && showBoardsCopy && boards.length >= pillsThreshold ? <BoardPeopleRow boards={boards} /> : null}
        {showRedirectLost ? (
          <RedirectLostNotice role="alert" data-testid="v4-signin-redirect-lost">
            {stalledProviderLabel
              ? `Continuing with ${stalledProviderLabel} didn’t finish.`
              : 'That sign-in didn’t finish.'}{' '}
            Log in with your email and password below instead.
          </RedirectLostNotice>
        ) : null}
        {showAppleFailed ? (
          <RedirectLostNotice role="alert" data-testid="v4-signin-apple-failed">
            Continuing with Apple didn’t finish. Log in with your email and
            password below instead.
          </RedirectLostNotice>
        ) : null}
        {browser !== 'webview' ? <>
          <ProviderBtn type="button" data-signin-entry="v4-modal-google" disabled={busy || !legal.document}
            onClick={() => runOAuth(v4SignInWithGoogle, 'google')}>
            <ProviderMark><GoogleMark /></ProviderMark>Continue with Google
          </ProviderBtn>
          <ProviderBtn type="button" data-signin-entry="v4-modal-apple" disabled={busy || !legal.document || (!appleRequest && !appleLoadFailed)}
            onClick={() => {
              if (!appleRequest) { setAppleAttempt(n => n + 1); return; }
              const request = appleRequest;
              setAppleRequest(null);
              void runOAuth(() => v4SignInWithApple(request, undefined, appleExistingAccount), 'apple').finally(() => setAppleAttempt(n => n + 1));
            }}>
            <ProviderMark><AppleMark /></ProviderMark>Continue with Apple
          </ProviderBtn>
          {appleLoadFailed ? <Subhead role="status">Apple sign-in couldn’t load. Tap Continue with Apple to retry.</Subhead> : null}
          <Divider aria-hidden="true">or</Divider>
        </> : null}
        <form onSubmit={onEmailSubmit} noValidate>
          <Field type="email" inputMode="email" autoComplete="email"
            placeholder="Email address" aria-label="Email address" value={email} disabled={busy}
            onChange={(e) => { setEmail(e.target.value); setResetSent(false); }} />
          {emailSuggestion ? (
            <SuggestRow data-testid="v4-modal-suggestion">
              Did you mean{' '}
              <button type="button" disabled={busy} onClick={() => setEmail(emailSuggestion)}>
                {emailSuggestion}
              </button>
              ?
            </SuggestRow>
          ) : null}
          <PasswordRow>
            <PasswordInput type={showPassword ? 'text' : 'password'}
              autoComplete={mode === 'create' ? 'new-password' : 'current-password'}
              placeholder="Password" aria-label="Password" value={password} disabled={busy}
              onChange={(e) => setPassword(e.target.value)} />
            <ShowHideBtn type="button" aria-pressed={showPassword}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              onClick={() => setShowPassword((v) => !v)}>
              {showPassword ? 'Hide' : 'Show'}
            </ShowHideBtn>
          </PasswordRow>
          {mode === 'create' ? (
            <Field type={showPassword ? 'text' : 'password'} autoComplete="new-password"
              placeholder="Confirm password" aria-label="Confirm password"
              value={confirmPassword} disabled={busy}
              onChange={(e) => setConfirmPassword(e.target.value)} />
          ) : null}
          <PrimaryBtn type="submit" data-signin-entry="v4-modal-email" disabled={busy || !legal.document || !EMAIL_RE.test(email.trim()) || !password}>
            {busy ? 'Please wait…' : mode === 'login' ? 'Log in' : 'Create account'}
          </PrimaryBtn>
        </form>
        <LegalNotice {...legal} variant={noticeVariant} />
        <TextLink type="button" disabled={busy} onClick={() => {
          setMode(mode === 'login' ? 'create' : 'login');
          setConfirmPassword('');
          setError(null);
          setResetSent(false);
        }}>
          {mode === 'login' ? 'New to Thea? Create account' : 'Already have an account? Log in'}
        </TextLink>
        <TextLink type="button" disabled={busy} onClick={resetPassword}>
          Forgot or need to set a password?
        </TextLink>
        {resetSent ? <Subhead role="status">If an account exists for this email, you’ll receive a link to set your Thea password.</Subhead> : null}
        {error ? <ErrorText role="alert">{error}</ErrorText> : null}
        <TextLink type="button" onClick={() => dismiss('not_now')}>Not now</TextLink>
      </Card>
    </Scrim>, document.body,
  );
};

export default V4SignInModal;
